import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { blogPostsQueryOptions } from "@/api-queries/blog";
import { Img } from "@/components/ui/img";
import { GeneratedCover } from "@/components/blog/GeneratedCover";
import { readingMinutes } from "@/lib/reading-time";
import { isBlogCategory, BLOG_CATEGORY_LABELS } from "@/lib/blog-categories";
import { blogCardExcerpt } from "@/lib/blog-excerpt";

const PREVIEW_LIMIT = 4;

const ACCENT = "oklch(37.12% 0.059 186.62)";
const DOT = "oklch(74.72% 0.089 82.39)";
const CARD_BORDER = "oklch(82% 0.03 84)";

interface ArticlePost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  readingMinutes?: number;
  category?: string | null;
  coverImageUrl?: string | null;
}

function PostCover({ post, className }: { post: ArticlePost; className: string }) {
  return post.coverImageUrl ? (
    <Img
      src={post.coverImageUrl}
      alt={post.title}
      sizes="(min-width: 1024px) 420px, (min-width: 640px) 50vw, 100vw"
      className={className}
      loading="lazy"
    />
  ) : (
    <GeneratedCover slug={post.slug} category={post.category} className={className} />
  );
}

function MetaRow({ post }: { post: ArticlePost }) {
  const label = isBlogCategory(post.category) ? BLOG_CATEGORY_LABELS[post.category] : null;
  const minutes = post.readingMinutes ?? (post.content ? readingMinutes(post.content) : null);
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.12em]"
      style={{ color: "oklch(45% 0.02 84)" }}
    >
      {label && <span style={{ color: ACCENT }}>{label}</span>}
      {label && minutes && <span className="size-1 rounded-full" style={{ background: DOT }} />}
      {minutes && <span>{minutes} min</span>}
    </div>
  );
}

export function TherapistArticles({ therapistId }: { therapistId: string }) {
  const { t } = useTranslation();

  const { data } = useQuery(
    blogPostsQueryOptions({
      therapistId,
      status: "published",
      limit: PREVIEW_LIMIT,
    }),
  );

  const posts: ArticlePost[] = data?.data ?? [];
  const total: number = data?.total ?? posts.length;

  if (posts.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-2.5">
        <h2
          className="text-2xl font-medium text-foreground sm:text-3xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("therapistDetail.articlesTitle")}
        </h2>
        <span
          className="rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            background: "oklch(37.12% 0.059 186.62 / 0.12)",
            color: "oklch(50% 0.15 84)",
          }}
        >
          {total}
        </span>
      </div>

      <div className="mt-5 grid gap-6 sm:mt-6 sm:grid-cols-2">
        {posts.map((post) => (
          <ArticleCard key={post.id} post={post} />
        ))}
      </div>

      {total > posts.length && (
        <div className="mt-6 flex justify-center">
          <Link
            to="/blog"
            search={{ therapist: therapistId }}
            className="inline-flex items-center justify-center rounded-full border border-border px-7 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
          >
            {t("therapistDetail.articlesViewAll", { count: total })}
          </Link>
        </div>
      )}
    </section>
  );
}

function ArticleCard({ post }: { post: ArticlePost }) {
  const excerpt = blogCardExcerpt(post);

  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex flex-col overflow-hidden rounded-3xl border bg-white transition-shadow hover:shadow-lg"
      style={{ borderColor: CARD_BORDER }}
    >
      <div className="aspect-[16/9] overflow-hidden">
        <PostCover
          post={post}
          className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <MetaRow post={post} />
        <h3
          className="mt-2 italic"
          style={{
            fontFamily: "var(--font-heading)",
            fontWeight: 400,
            fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)",
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
          }}
        >
          {post.title}
        </h3>
        {excerpt && (
          <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">
            {excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}
